/**
 * Belege ansehen — und die Zuordnungen geraderücken, die unterwegs verrutscht
 * sind.
 *
 * **Der Beleg selbst.** Das Foto ist die Arbeitskopie. Das Papieroriginal
 * kommt weiter ins Büro — es gibt kein ersetzendes Scannen, und diese
 * Entscheidung steht. Das Bild dient dazu, abends zu wissen, ob der Nachweis
 * da ist, ohne bis Freitag auf die Mappe zu warten.
 *
 * **Der Zuordnungskonflikt.** Die Warteschlange auf dem Handy lehnt nie etwas
 * ab. Kommt ein Beleg herein, dessen Auftrag inzwischen umdisponiert wurde,
 * wird er trotzdem angenommen, am Auftrag festgemacht und der Fall gemeldet.
 * Ohne einen Ort, an dem diese Meldungen auflaufen, wäre „nie ablehnen" eine
 * Regel, die Daten still an der falschen Stelle ablegt. Der Ort ist seit dem
 * Umbau „Offene Punkte" — zusammen mit allem anderen, was liegen geblieben ist.
 *
 * Die Bilder liegen in einem nicht öffentlichen Speicher. Angezeigt werden sie
 * über Adressen, die nach fünf Minuten verfallen — lang genug zum Anschauen,
 * kurz genug, dass eine kopierte Adresse in einer E-Mail nichts mehr nützt.
 *
 * ═══ WAS HIER FRÜHER STAND ═══
 *
 * Eine Funktion `belegeLaden`, die alle Belege nach Auftrag bündelte. Sie
 * gehörte zum alten Bildschirm „Belege" und ist mit ihm weggefallen: Belege
 * ansehen gehört an die Tour, wo sie hingehören, und was fehlt, steht unter
 * „Offene Punkte". Eine Funktion, die niemand mehr ruft, stehen zu lassen ist
 * die verlässlichste Art, in zwei Jahren nicht mehr zu wissen, was gilt.
 */

import { supabase } from '../supabase';
import { Speicherfehler } from './speichern';
import { alsKlarfehler } from './fehler';

export type Dokumentart =
  | 'eingangslieferschein'
  | 'ausgangslieferschein'
  | 'nachweis'
  | 'auftragsdokument'
  | 'sonstiges';

export const ARTTEXT: Record<Dokumentart, string> = {
  eingangslieferschein: 'Eingangslieferschein',
  ausgangslieferschein: 'Ausgangslieferschein',
  nachweis: 'Nachweis',
  auftragsdokument: 'Auftragsdokument',
  sonstiges: 'Sonstiges',
};

/**
 * Der Pfad INNERHALB des Eimers.
 *
 * ═══ WARUM DIESE ZEILE ÜBERHAUPT NÖTIG IST ═══
 *
 * `createSignedUrl` erwartet den Pfad ohne den Namen des Eimers — „2026/08/27/
 * abc.jpg", nicht „belege/2026/08/27/abc.jpg". Beides sieht beim Lesen gleich
 * richtig aus, und in einer Handvoll früher eingespielter Zeilen stand die
 * zweite Schreibweise. Die Antwort darauf ist „Object not found", also
 * ausgerechnet die Meldung, die aussieht, als wäre die Datei weg.
 *
 * Hier wird der Präfix abgeschnitten, statt die Daten einmal zu putzen: Eine
 * Zeile, die in zwei Jahren wieder mit Präfix hereinkommt — aus einem Import,
 * aus einem Skript —, würde sonst denselben Abend kosten.
 */
function imEimer(pfad: string): string {
  return pfad.replace(/^belege\//, '');
}

/**
 * Eine Adresse zum Anschauen, die nach fünf Minuten verfällt.
 *
 * Der Speicher ist nicht öffentlich; ohne diese Signatur kommt niemand an das
 * Bild — auch nicht mit der richtigen Adresse.
 *
 * Die Fehlermeldung unterscheidet zwei Fälle, weil man sie unterschiedlich
 * behandelt: Ist die DATEI nicht da, hilft kein Wiederholen — der Eintrag ist
 * ohne Bild und gehört verworfen. Ist es etwas anderes (kein Netz, keine
 * Berechtigung), lohnt ein zweiter Versuch.
 */
export async function belegAdresse(pfad: string): Promise<string> {
  const { data, error } = await supabase.storage
    .from('belege')
    .createSignedUrl(imEimer(pfad), 300);

  if (error || !data?.signedUrl) {
    const meldung = error?.message ?? 'keine signierte Adresse';
    const dateiFehlt = /not\s*found|does not exist/i.test(meldung);

    throw new Speicherfehler({
      art: dateiFehlt ? 'regel' : 'unbekannt',
      titel: dateiFehlt
        ? 'Zu diesem Eintrag gibt es keine Datei'
        : 'Das Bild lässt sich gerade nicht öffnen',
      text: dateiFehlt
        ? 'Der Eintrag steht in der Liste, im Speicher liegt aber nichts dazu. Das passiert bei Testdaten und bei Fotos, die beim Hochladen abgebrochen sind. Über „Verwerfen" verschwindet der Eintrag — das Papieroriginal bleibt davon unberührt.'
        : meldung,
      original: `${meldung} (Pfad: ${pfad})`,
    });
  }
  return data.signedUrl;
}

/* ------------------------------------------------------------- Konflikte */

/**
 * Erledigt heißt: Jemand hat hingeschaut und entschieden. Nicht: Es hat sich
 * von selbst gelöst. Deshalb wird eine Notiz mitgeschrieben — in einem halben
 * Jahr ist die Frage nie „gab es einen Konflikt", sondern „was hat man damals
 * daraus gemacht".
 *
 * ═══ NUR, WAS NOCH OFFEN IST (H1.6) ═══
 *
 * Vorher traf das Update jeden Konflikt mit dieser Kennung — auch einen, den
 * eine Kollegin eine Minute vorher erledigt hatte. Ihre Notiz war danach
 * still überschrieben, und die Frage „was hat man damals daraus gemacht"
 * hatte die falsche Antwort. Jetzt nur `erledigt_am is null`, und `.select()`
 * sagt, ob eine Zeile getroffen wurde: false ist kein Erfolg.
 *
 * WER und WANN erledigt wurde, setzt ein Auslöser in der Datenbank
 * (`sync_konflikt_erledigt_stempeln`) — ein mitgeschickter Wert wäre eine
 * Behauptung, und eine falsch gehende Büro-Uhr ein falscher Zeitpunkt. Der
 * Zeitpunkt hier ist nur das Signal „jetzt erledigt"; der Server ersetzt ihn.
 */
export async function konfliktErledigen(
  id: string,
  notiz: string,
): Promise<void> {
  const { data, error } = await supabase
    .from('sync_konflikt')
    .update({ erledigt_am: new Date().toISOString(), erledigt_notiz: notiz.trim() })
    .eq('id', id)
    .is('erledigt_am', null)
    .select('id');
  if (error) throw new Speicherfehler(alsKlarfehler(error));
  if (data && data.length > 0) return;

  // Keine Zeile getroffen — nachsehen, warum. Wie in `aendern()`: Die beiden
  // Fälle verlangen verschiedene Antworten, also wird nicht geraten.
  const { data: vorhanden, error: nachfrageFehler } = await supabase
    .from('sync_konflikt')
    .select('erledigt_am, erledigt_von, erledigt_notiz')
    .eq('id', id)
    .maybeSingle();
  // Scheitert die Nachfrage, wissen wir nicht, warum nichts getroffen wurde —
  // „nicht mehr vorhanden" wäre geraten.
  if (nachfrageFehler) throw new Speicherfehler(alsKlarfehler(nachfrageFehler));

  if (vorhanden?.erledigt_am) {
    throw new Speicherfehler({
      art: 'gleichzeitig_geaendert',
      titel: 'Schon erledigt',
      text: await schonErledigtText(vorhanden),
      original: 'sync_konflikt.erledigt_am war beim Speichern schon gesetzt',
    });
  }

  throw new Speicherfehler({
    art: 'regel',
    titel: 'Nicht mehr vorhanden',
    text: 'Dieser Zuordnungskonflikt ist nicht mehr da — entweder gelöscht, oder dein Konto darf ihn nicht mehr sehen. Deine Notiz wurde NICHT gespeichert.',
    original: 'kein offener sync_konflikt mit dieser Kennung sichtbar',
  });
}

/**
 * Wer, wann, was — soweit es lesbar ist. Ein Name, der sich nicht nachschlagen
 * lässt (ältere Einträge ohne `erledigt_von`, oder die Zeilensicherheit), fällt
 * still weg; die Meldung bleibt trotzdem wahr.
 */
async function schonErledigtText(erledigt: {
  erledigt_am: string;
  erledigt_von: string | null;
  erledigt_notiz: string | null;
}): Promise<string> {
  let wer = '';
  if (erledigt.erledigt_von) {
    const { data: person } = await supabase
      .from('mitarbeiter')
      .select('vorname, nachname')
      .eq('id', erledigt.erledigt_von)
      .maybeSingle();
    if (person) wer = ` von ${person.vorname} ${person.nachname}`;
  }
  const wann = new Date(erledigt.erledigt_am).toLocaleString('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
  const notiz = erledigt.erledigt_notiz ? ` Notiz: „${erledigt.erledigt_notiz}“.` : '';
  return `Dieser Zuordnungskonflikt wurde am ${wann}${wer} erledigt.${notiz} Deine Notiz wurde NICHT gespeichert.`;
}
